import { useEffect, useRef, useState } from 'react';
import type { Configuracion, TurnoPrueba, VersionResumida } from '../../api/ops/asistente';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError } from '../../logica/estados';
import { useCabecera, type AccionCabecera } from '../../marco/cabecera';
import type { PropsApartado } from './comun';
import { Pliegue } from './Configuracion';
import { autorDe, cambios, comoEscritura, enLista, escritura, fechaCorta, nombreIdioma, nombreTono, probadoTrasElCambio, resumenCanales, resumenHoras } from './reglas';
import { AYUDA, PIE_PRUEBA, SUGERENCIAS } from './textos';

/**
 * Probar y publicar (11.7): conversación de prueba (`startAssistantTestSession` / `sendAssistantTestMessage`) con el
 * borrador o con la versión publicada, **siempre como visitante** (G544), con las herramientas usadas y las que no se
 * ejecutan por ser una prueba (`blocked_tools`, G592). A la derecha, plegados, el Borrador (qué cambia, si se probó
 * después del último cambio con `TestSession.started_at` frente a `ConfigVersion.updated_at` (G688), avisos que no
 * bloquean, G546, y «Descartar los cambios») y las Versiones (`listAssistantVersions`, ver una y volver a ella). Publicar
 * con `publishAssistantDraft` e `If-Match` del borrador.
 */
const MAX_TURNOS = 30;
const PIDE_PERSONA = new Set(['handed_over', 'cut_quota']);

export function Probar({ datos, ops, negocio, movil }: PropsApartado) {
    const avisar = useAvisoBreve();
    const b = datos.borrador.datos;
    const pub = datos.estado.published_version ?? null;
    const [que, setQue] = useState<'borrador' | 'publicada'>('borrador');
    const [idioma, setIdioma] = useState<string>(b.locales[0] ?? 'es-ES');
    const [sesion, setSesion] = useState<{ id: string; inicio: string | null } | null>(null);
    const [turnos, setTurnos] = useState<TurnoPrueba[]>([]);
    const [escribiendo, setEscribiendo] = useState<string | null>(null);
    const [texto, setTexto] = useState('');
    const [abierto, setAbierto] = useState<string | null>('borrador');
    const [versiones, setVersiones] = useState<VersionResumida[] | null>(null);
    const [cursor, setCursor] = useState<{ siguiente: string | null; pila: (string | null)[] }>({ siguiente: null, pila: [null] });
    const [version, setVersion] = useState<Configuracion | null>(null);
    const [ventana, setVentana] = useState<'publicar' | 'descartar' | 'volver' | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const fin = useRef<HTMLDivElement | null>(null);

    const pagina = cursor.pila[cursor.pila.length - 1] ?? null;
    useEffect(() => {
        let vivo = true;
        ops.listAssistantVersions(negocio, { cursor: pagina, limit: 10 })
            .then((r) => {
                if (vivo) {
                    setVersiones(r.data);
                    setCursor((c) => ({ ...c, siguiente: r.next_cursor }));
                }
            })
            .catch(() => { if (vivo) { setVersiones([]); } });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pagina, pub?.id]);

    useEffect(() => {
        const caja = fin.current;
        if (caja !== null) {
            caja.scrollTop = caja.scrollHeight;
        }
    }, [turnos.length, escribiendo]);

    const siguiente = (pub?.number ?? 0) + 1;
    const hayCambios = pub === null || datos.estado.has_unpublished_changes;
    const lista = cambios(datos.publicada === null ? null : comoEscritura(datos.publicada), escritura(b), datos.canales ?? []);
    const avisos = b.instruction_warnings ?? [];
    const lleno = turnos.length >= MAX_TURNOS;
    const nombre = (que === 'publicada' ? datos.publicada?.assistant_name : b.assistant_name) || 'Tu asistente';
    const probado = probadoTrasElCambio(b.updated_at, datos.pruebaDelBorrador);

    const nueva = () => {
        setSesion(null);
        setTurnos([]);
        setTexto('');
    };

    const acciones: AccionCabecera[] = [];
    if (turnos.length > 0) {
        acciones.push({ clave: 'nueva', texto: 'Nueva prueba', icono: 'rotate-ccw', secundaria: true, onClick: nueva });
    }
    if (datos.publicar) {
        acciones.push({ clave: 'publicar', texto: `Publicar versión ${String(siguiente)}`, icono: 'rocket', desactivado: !hayCambios || datos.guardando, onClick: () => { setVentana('publicar'); } });
    }
    useCabecera({ ayuda: AYUDA.probar, acciones });

    const enviar = async (t: string) => {
        const limpio = t.trim();
        if (limpio === '' || escribiendo !== null || lleno || !datos.probar) {
            return;
        }
        setTexto('');
        setEscribiendo(limpio);
        try {
            let actual = sesion;
            if (actual === null) {
                const s = await ops.startAssistantTestSession(negocio, { ...(que === 'publicada' && pub !== null ? { version_id: pub.id } : {}), locale: idioma });
                actual = { id: s.id, inicio: s.version_id == null ? (s.started_at ?? null) : null };
                setSesion(actual);
            }
            const turno = await ops.sendAssistantTestMessage(negocio, actual.id, limpio);
            setTurnos((x) => [...x, turno]);
            if (actual.inicio !== null) {
                datos.fijarPruebaDelBorrador(actual.inicio);
            }
        } catch (e) {
            setTexto(limpio);
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'El asistente no ha podido responder. Inténtalo otra vez.' });
        } finally {
            setEscribiendo(null);
        }
    };

    const publicar = async () => {
        setOcupado(true);
        try {
            await ops.publishAssistantDraft(negocio, datos.borrador.version ?? '');
            setVentana(null);
            avisar({ tipo: 'ok', texto: `Versión ${String(siguiente)} publicada.` });
            datos.recargar();
        } catch (e) {
            avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.codigo === 'version_conflict' ? 'Alguien ha cambiado el borrador. Revísalo y vuelve a publicar.' : (avisoBreveDeError(e) ?? 'No se ha podido publicar. Inténtalo otra vez.') });
            if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                datos.recargar();
            }
        } finally {
            setOcupado(false);
        }
    };

    const restaurar = async (id: string, hecho: string) => {
        setOcupado(true);
        try {
            const r = await ops.restoreAssistantVersion(negocio, id);
            datos.nuevoBorrador(r);
            setVentana(null);
            setVersion(null);
            avisar({ tipo: 'ok', texto: hecho });
        } catch (e) {
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido cambiar el borrador. Inténtalo otra vez.' });
        } finally {
            setOcupado(false);
        }
    };

    const abrirVersion = (v: VersionResumida) => {
        ops.getAssistantVersion(negocio, v.id)
            .then(setVersion)
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido abrir la versión.' }); });
    };

    const igualQueBorrador = version !== null && JSON.stringify(escritura(version)) === JSON.stringify(escritura(b));
    const pliega = (k: string) => () => { setAbierto((a) => (a === k ? null : k)); };

    return (
        <div className="rv-as-config">
            <section className="rv-as-tarjeta rv-as-prueba" aria-label="Conversación de prueba">
                <div className="rv-as-prueba__cab">
                    <span className="rv-as-baldosa rv-as-baldosa--tinta">
                        <Icono nombre="bot" />
                    </span>
                    <span className="rv-as-prueba__titulo">
                        <strong>{`${nombre} · prueba`}</strong>
                        <small>{`${que === 'publicada' && pub !== null ? `Versión ${String(pub.number)}` : 'Borrador'} · como visitante`}</small>
                    </span>
                    <Chip estado="neutral">{`${String(turnos.length)} de ${String(MAX_TURNOS)}`}</Chip>
                </div>
                <div className="rv-as-prueba__elige">
                    <Desplegable
                        etiqueta="Qué pruebas"
                        valor={que}
                        opciones={[{ valor: 'borrador', texto: 'El borrador' }, ...(pub === null ? [] : [{ valor: 'publicada', texto: `La versión ${String(pub.number)} (publicada)` }])]}
                        onChange={(v) => {
                            setQue(v === 'publicada' ? 'publicada' : 'borrador');
                            nueva();
                        }}
                    />
                    <Desplegable
                        etiqueta="Idioma"
                        valor={idioma}
                        opciones={b.locales.map((l) => ({ valor: l, texto: nombreIdioma(l) }))}
                        onChange={(v) => {
                            setIdioma(v);
                            nueva();
                        }}
                    />
                </div>
                <div className="rv-as-chat" aria-live="polite" ref={fin}>
                    {turnos.length === 0 && escribiendo === null && (
                        <div className="rv-as-intro">
                            <p>{que === 'publicada' && pub !== null ? `Pruebas la versión ${String(pub.number)}, la que ven tus clientes ahora.` : 'Pruebas el borrador con tus datos reales. Solo consulta: no se reserva ni se cobra nada.'}</p>
                            {datos.probar && (
                                <div className="rv-as-sugerencias">
                                    {SUGERENCIAS.map((s) => (
                                        <button key={s} type="button" className="rv-as-sugerencia" onClick={() => { void enviar(s); }}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {turnos.map((t, i) => (
                        <div key={i} className="rv-as-turno">
                            <p className="rv-as-burbuja rv-as-burbuja--yo">{t.user_text}</p>
                            <span className="rv-as-quien-habla">
                                <Icono nombre="bot" />
                                {nombre}
                            </span>
                            <p className="rv-as-burbuja">{t.reply}</p>
                            {PIDE_PERSONA.has(t.outcome) && <span className="rv-as-validar">Texto a validar</span>}
                            {t.tool_calls.length > 0 && (
                                <ul className="rv-as-usadas" aria-label="Herramientas usadas">
                                    {t.tool_calls.map((c, j) => (
                                        <li key={j}>
                                            <Icono nombre={c.problem_code == null ? 'search' : 'circle-alert'} />
                                            <code>{c.tool_code}</code>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(t.blocked_tools ?? []).length > 0 && (
                                <p className="rv-as-nota rv-as-nota--ambar" role="note">
                                    <span>
                                        <Icono nombre="shield-check" />
                                        <span>{`En la prueba no se hace: ${enLista(t.blocked_tools ?? [])}. No se pide código ni se reserva nada.`}</span>
                                    </span>
                                </p>
                            )}
                        </div>
                    ))}
                    {escribiendo !== null && (
                        <div className="rv-as-turno">
                            <p className="rv-as-burbuja rv-as-burbuja--yo">{escribiendo}</p>
                            <span className="rv-as-escribiendo" role="status" aria-label={`${nombre} está escribiendo`}>
                                <i />
                                <i />
                                <i />
                            </span>
                        </div>
                    )}
                </div>
                {datos.probar && (
                    <form
                        className="rv-as-escribir"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void enviar(texto);
                        }}
                    >
                        <Campo etiqueta={lleno ? 'Prueba completa: empieza otra' : 'Escribe como un cliente'} valor={texto} max={2000} bloqueado={lleno} mayusculas="sentences" onChange={setTexto} onEnter={() => { void enviar(texto); }} />
                        <Boton icono="send" movil={movil} disabled={texto.trim() === '' || escribiendo !== null || lleno} onClick={() => { void enviar(texto); }}>
                            Enviar
                        </Boton>
                    </form>
                )}
                <small className="rv-as-ayuda">{PIE_PRUEBA}</small>
            </section>

            <div className="rv-as-pliegues">
                <Pliegue
                    icono="pencil"
                    titulo="Borrador"
                    resumen={pub === null ? 'Primera versión' : hayCambios ? `${String(lista.length)} ${lista.length === 1 ? 'cambio' : 'cambios'} desde la versión ${String(pub.number)}` : `Igual que la versión ${String(pub.number)}`}
                    chip={pub !== null && hayCambios ? 'Sin publicar' : ''}
                    abierto={abierto === 'borrador'}
                    onAlternar={pliega('borrador')}
                >
                    <p className="rv-as-ayuda">
                        {pub === null
                            ? 'Pruébalo y publícalo para que tu asistente empiece a atender.'
                            : hayCambios
                              ? `Tus clientes siguen con la versión ${String(pub.number)} hasta que publiques.`
                              : 'Cuando cambies algo en Configuración o en Lo que sabe, verás aquí qué cambia antes de publicarlo.'}
                    </p>
                    {hayCambios && <Cambios lista={lista} />}
                    {hayCambios && (
                        <p className={`rv-as-nota rv-as-nota--${probado ? 'gris' : 'ambar'}`}>
                            <span>
                                <Icono nombre={probado ? 'check-check' : 'message-circle'} />
                                <span>{probado ? 'Lo has probado después del último cambio.' : 'Todavía no lo has probado desde el último cambio. Escríbele unos mensajes antes de publicar.'}</span>
                            </span>
                        </p>
                    )}
                    {hayCambios && avisos.length > 0 && (
                        <button type="button" className="rv-as-nota rv-as-nota--ambar rv-as-nota--boton" onClick={() => { datos.irA('instrucciones'); }}>
                            <span>
                                <Icono nombre="triangle-alert" />
                                <span>{`Las instrucciones tienen ${String(avisos.length)} ${avisos.length === 1 ? 'aviso' : 'avisos'}. Revísalas antes de publicar.`}</span>
                            </span>
                        </button>
                    )}
                    {datos.editar && pub !== null && hayCambios && (
                        <>
                            <Boton tipo="destructivo" icono="rotate-ccw" movil={movil} onClick={() => { setVentana('descartar'); }}>
                                Descartar los cambios
                            </Boton>
                            <small className="rv-as-ayuda">{`El borrador vuelve a ser la versión ${String(pub.number)}. Los contactos sin asistente no cambian.`}</small>
                        </>
                    )}
                </Pliegue>

                <Pliegue
                    icono="history"
                    titulo="Versiones"
                    resumen={versiones === null ? 'Cargando…' : versiones.length === 0 && pagina === null ? 'Todavía ninguna publicada' : pub === null ? '' : `Tus clientes ven la ${String(pub.number)}`}
                    abierto={abierto === 'versiones'}
                    onAlternar={pliega('versiones')}
                >
                    {versiones !== null && versiones.length === 0 && pagina === null ? (
                        <p className="rv-as-ayuda">Todavía no has publicado ninguna versión. Cuando publiques la primera, tu asistente empezará a atender.</p>
                    ) : (
                        <>
                            <p className="rv-as-ayuda">Cada publicación queda guardada. Puedes volver a cualquiera.</p>
                            {(versiones ?? []).map((v) => (
                                <button key={v.id} type="button" className="rv-as-version" onClick={() => { abrirVersion(v); }}>
                                    <span className={`rv-as-baldosa${v.id === pub?.id ? ' rv-as-baldosa--tinta' : ''}`}>{v.number}</span>
                                    <span>
                                        <strong>{`Versión ${String(v.number)}`}</strong>
                                        <small>{[fechaCorta(v.published_at), autorDe(v.published_by_member_id, v.published_by_name, datos.equipo)].filter((t) => t !== null).join(' · ')}</small>
                                    </span>
                                    <Chip estado={v.id === pub?.id ? 'confirmed' : 'neutral'}>{v.id === pub?.id ? 'Publicada' : 'Archivada'}</Chip>
                                </button>
                            ))}
                            {(cursor.pila.length > 1 || cursor.siguiente !== null) && (
                                <div className="rv-as-paginas">
                                    <Boton tipo="fantasma" icono="chevron-left" disabled={cursor.pila.length <= 1} onClick={() => { setCursor((c) => ({ ...c, pila: c.pila.slice(0, -1) })); }}>
                                        Anterior
                                    </Boton>
                                    <Boton tipo="fantasma" icono="chevron-right" iconoDetras disabled={cursor.siguiente === null} onClick={() => { setCursor((c) => ({ ...c, pila: [...c.pila, c.siguiente] })); }}>
                                        Siguiente
                                    </Boton>
                                </div>
                            )}
                        </>
                    )}
                </Pliegue>
            </div>

            <PanelLateral
                abierto={version !== null}
                onCerrar={() => { setVersion(null); }}
                movil={movil}
                tamano="ficha"
                eyebrow={version?.id === pub?.id ? 'Publicada' : 'Archivada'}
                icono="history"
                titulo={version === null ? '' : `Versión ${String(version.number ?? '')}`}
                subtitulo={version?.published_at == null ? undefined : [fechaCorta(version.published_at), autorDe(version.published_by_member_id, version.published_by_name, datos.equipo)].filter((t) => t !== null).join(' · ')}
                accion={datos.editar && !igualQueBorrador ? 'Volver a esta versión' : ''}
                accionIcono="rotate-ccw"
                onAccion={() => { setVentana('volver'); }}
            >
                {version !== null && (
                    <div className="rv-as-panel">
                        <dl className="rv-as-datos">
                            <dt>Nombre</dt>
                            <dd>{version.assistant_name}</dd>
                            <dt>Tono</dt>
                            <dd>{nombreTono(version.tone)}</dd>
                            <dt>Idiomas</dt>
                            <dd>{enLista(version.locales.map(nombreIdioma))}</dd>
                            <dt>Canales</dt>
                            <dd>{resumenCanales(version.channel_ids ?? [], datos.canales ?? []).texto}</dd>
                            <dt>Atención humana</dt>
                            <dd>{resumenHoras(version.human_hours ?? [])}</dd>
                            <dt>Instrucciones</dt>
                            <dd>{(version.instructions ?? '') === '' ? 'Sin instrucciones' : version.instructions}</dd>
                        </dl>
                        {(version.faqs ?? []).length > 0 && (
                            <div className="rv-as-bloque">
                                <strong>Preguntas frecuentes</strong>
                                {(version.faqs ?? []).map((f, i) => (
                                    <p key={i} className="rv-as-faq">
                                        <strong>{f.question}</strong>
                                        <span>{f.answer}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                        <p className="rv-as-ayuda">{igualQueBorrador ? 'Tu borrador es igual que esta versión.' : 'Una versión publicada no se cambia. Volver a ella copia su contenido en el borrador; para que valga, pruébalo y publícalo.'}</p>
                    </div>
                )}
            </PanelLateral>

            <Ventana
                abierta={ventana === 'publicar'}
                titulo={`Publicar la versión ${String(siguiente)}`}
                texto={pub === null ? 'Será la primera versión de tu asistente. Después podrás encenderlo.' : `Tu asistente la usará desde el siguiente mensaje de cada conversación. La versión ${String(pub.number)} queda guardada por si quieres volver.`}
                accion="Publicar"
                accionIcono="rocket"
                ocupado={ocupado}
                ocupadoTexto="Publicando…"
                movil={movil}
                onAccion={() => { void publicar(); }}
                onCerrar={() => { setVentana(null); }}
            >
                <Cambios lista={lista} />
                {(avisos.length > 0 || !probado) && (
                    <p className="rv-as-nota rv-as-nota--ambar">
                        <span>
                            <Icono nombre="triangle-alert" />
                            <span>{[avisos.length > 0 ? `Las instrucciones tienen ${String(avisos.length)} ${avisos.length === 1 ? 'aviso' : 'avisos'}.` : '', probado ? '' : 'No lo has probado desde el último cambio.'].filter((x) => x !== '').join(' ')}</span>
                        </span>
                    </p>
                )}
            </Ventana>
            <Ventana
                abierta={ventana === 'descartar'}
                titulo="Descartar los cambios del borrador"
                texto={pub === null ? '' : `El borrador vuelve a ser la versión ${String(pub.number)}. Los contactos sin asistente no cambian.`}
                accion="Descartar"
                accionIcono="rotate-ccw"
                ocupado={ocupado}
                ocupadoTexto="Descartando…"
                movil={movil}
                onAccion={() => { if (pub !== null) { void restaurar(pub.id, 'Cambios descartados.'); } }}
                onCerrar={() => { setVentana(null); }}
            />
            <Ventana
                abierta={ventana === 'volver' && version !== null}
                titulo={`Volver a la versión ${String(version?.number ?? '')}`}
                texto={`Su contenido sustituye a tu borrador actual${hayCambios && pub !== null ? ' y pierdes los cambios sin publicar' : ''}. Para que valga, pruébalo y publícalo como versión nueva.`}
                accion="Volver a esta versión"
                accionIcono="rotate-ccw"
                ocupado={ocupado}
                movil={movil}
                onAccion={() => { if (version?.id != null) { void restaurar(version.id, `El borrador es ahora la versión ${String(version.number ?? '')}.`); } }}
                onCerrar={() => { setVentana(null); }}
            />
        </div>
    );
}

function Cambios({ lista }: { lista: readonly { icono: NombreIcono; texto: string }[] }) {
    if (lista.length === 0) {
        return null;
    }
    return (
        <ul className="rv-as-cambios">
            {lista.map((c) => (
                <li key={c.texto}>
                    <Icono nombre={c.icono} />
                    <span>{c.texto}</span>
                </li>
            ))}
        </ul>
    );
}
